import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DEFAULT_ADJUSTMENTS,
    EXPORT_MAX_DIM,
    PREVIEW_MAX_DIM,
    downscaleImageData,
    imageDataToBlob,
    imageDataToCanvas,
    imageDataToDataUrl,
    imageToImageData,
    loadDocumentImage,
    processDocument,
    rotateImageData,
    rotateQuad,
    warpDocumentQuad,
} from './documentCleanupEngine';
import { MAX_PAGES, makeThumbUrl, pagesFromFiles } from './documentCleanupPages';

/** Let the browser paint before a long synchronous pass. */
const nextFrame = () => new Promise((resolve) => setTimeout(resolve, 30));

/**
 * How many pages' pixels are kept decoded at once. Flipping between two pages
 * while comparing them is the common move, so a couple of neighbours stay warm;
 * everything else is re-decoded from its blob, which costs a few hundred
 * milliseconds and saves tens of megabytes per page.
 */
const PIXEL_CACHE_SIZE = 3;

/** A page nobody has touched: no preset and every slider at its default. */
const isUntouched = (page) => (!page.preset || page.preset === 'none')
    && Object.keys(DEFAULT_ADJUSTMENTS).every((key) => (page.adjustments?.[key] || 0) === (DEFAULT_ADJUSTMENTS[key] || 0));

/** What a rail thumbnail's edited copy was made from — the plain thumbnail plus the page's cleanup settings. */
const thumbSignature = (page) => `${page.thumbUrl.length}:${page.thumbUrl.slice(-24)}|${page.preset}|${JSON.stringify(page.adjustments)}`;

async function decodeThumb(url) {
    const img = new Image();
    img.src = url;
    await img.decode();
    return imageToImageData(img);
}

/**
 * The page list, the selected page's working pixels, and every edit that acts on
 * one page or on all of them.
 *
 * Pixels never live in React state: an ImageData in a setState is a re-render
 * carrying 40MB. State holds page METADATA (rotation, crop quad, preset,
 * sliders, thumbnail) and the pixels sit in a ref-held cache keyed by page id,
 * rebuilt whenever that page's geometry changes.
 *
 * `adjustThumbs` (opt-in): the rail normally shows each page as imported (rotated / cropped, nothing more). With this on every
 * edited page also gets a `thumbEditedUrl` — its thumbnail run through the same cleanup + tone pass as the preview, so
 * brightness / contrast / black-and-white show on the tile itself. The plain `thumbUrl` is never touched.
 */
export default function useDocumentCleanupPages({ onError, adjustThumbs = false } = {}) {
    const [pages, setPages] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [previewResult, setPreviewResult] = useState(null);
    const [sourceSize, setSourceSize] = useState(null);
    const [baseUrl, setBaseUrl] = useState(null);
    const [busy, setBusy] = useState(false);
    const [importing, setImporting] = useState(false);
    const [compare, setCompare] = useState(false);
    // Bumped whenever the selected page's pixels are rebuilt, so the preview
    // effect re-runs without the pixels themselves being state.
    const [sourceVersion, setSourceVersion] = useState(0);

    const cacheRef = useRef(new Map());
    const pagesRef = useRef(pages);
    pagesRef.current = pages;
    const selectedRef = useRef(selectedId);
    selectedRef.current = selectedId;
    const rebuildTokenRef = useRef(0);

    const selected = useMemo(
        () => pages.find((p) => p.id === selectedId) || null,
        [pages, selectedId],
    );

    const dropEntry = useCallback((id) => {
        const entry = cacheRef.current.get(id);
        if (!entry) return;
        if (entry.baseUrl) URL.revokeObjectURL(entry.baseUrl);
        cacheRef.current.delete(id);
    }, []);

    const trimCache = useCallback((keepId) => {
        const cache = cacheRef.current;
        while (cache.size > PIXEL_CACHE_SIZE) {
            const oldest = [...cache.keys()].find((k) => k !== keepId);
            if (!oldest) break;
            dropEntry(oldest);
        }
    }, [dropEntry]);

    useEffect(() => () => {
        [...cacheRef.current.keys()].forEach((id) => {
            const entry = cacheRef.current.get(id);
            if (entry?.baseUrl) URL.revokeObjectURL(entry.baseUrl);
        });
        cacheRef.current.clear();
    }, []);

    const patchPage = useCallback((id, patch) => {
        setPages((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    }, []);

    /**
     * Decode a page and derive everything downstream of its geometry:
     *   original → rotate → warp the crop quad out → preview-sized copy.
     * The crop is never baked into the stored pixels; re-opening the crop dialog
     * has to show the UNCROPPED page, or a second crop would crop the crop.
     */
    const buildEntry = useCallback(async (page) => {
        const cached = cacheRef.current.get(page.id);
        const original = cached?.original ?? imageToImageData(
            await loadDocumentImage(page.blob),
        );
        const base = page.rotation ? rotateImageData(original, page.rotation * 90) : original;
        const source = page.quad ? warpDocumentQuad(base, page.quad) : base;
        const previewSource = downscaleImageData(source, PREVIEW_MAX_DIM);

        if (cached?.baseUrl) URL.revokeObjectURL(cached.baseUrl);
        const blob = await imageDataToBlob(base, 'image/jpeg', 0.92);
        const entry = {
            original,
            base,
            source,
            previewSource,
            baseUrl: URL.createObjectURL(blob),
        };
        cacheRef.current.set(page.id, entry);
        trimCache(page.id);
        return entry;
    }, [trimCache]);

    /**
     * Make the selected page current: decode if needed, publish its base URL and
     * size, and let the preview effect re-run.
     *
     * Guarded by a token rather than a flag — a fast run down the rail must land
     * on the page the user stopped at, not on whichever decode finished last.
     */
    const refreshSelected = useCallback(async (pageOverride) => {
        const page = pageOverride
            || pagesRef.current.find((p) => p.id === selectedRef.current);
        if (!page) {
            setPreviewResult(null);
            setSourceSize(null);
            setBaseUrl(null);
            return;
        }

        const token = (rebuildTokenRef.current += 1);
        setBusy(true);
        try {
            const entry = await buildEntry(page);
            if (token !== rebuildTokenRef.current) return;
            setBaseUrl(entry.baseUrl);
            setSourceSize({ width: entry.source.width, height: entry.source.height });
            setSourceVersion((v) => v + 1);

            // The rail shows the page as it now stands — rotated, cropped — so a
            // thumbnail is refreshed from the same pass that built the pixels.
            const thumbUrl = await makeThumbUrl(
                imageDataToCanvas(entry.previewSource),
                entry.previewSource.width,
                entry.previewSource.height,
            );
            if (token === rebuildTokenRef.current) {
                patchPage(page.id, {
                    thumbUrl,
                    width: entry.source.width,
                    height: entry.source.height,
                });
            }
        } catch {
            if (token === rebuildTokenRef.current) onError?.('failed');
        } finally {
            if (token === rebuildTokenRef.current) setBusy(false);
        }
    }, [buildEntry, onError, patchPage]);

    // Selecting a page loads it; the compare toggle is per page, so it resets.
    useEffect(() => {
        setCompare(false);
        if (!selectedId) return;
        void refreshSelected();
        // refreshSelected reads the current page from refs, so it must not be a
        // dependency here or every metadata patch would re-decode.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    // Live preview of the selected page — debounced so slider drags stay smooth.
    useEffect(() => {
        const entry = cacheRef.current.get(selectedId);
        const page = selected;
        if (!entry || !page) return undefined;

        let cancelled = false;
        const timer = setTimeout(() => {
            try {
                const result = processDocument(entry.previewSource, {
                    preset: page.preset,
                    adjustments: page.adjustments,
                });
                if (!cancelled) setPreviewResult(result);
            } catch {
                if (!cancelled) onError?.('failed');
            }
        }, 90);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [selected?.preset, selected?.adjustments, selectedId, sourceVersion, onError, selected]);

    // Rail thumbnails that show the page as it will print (see `adjustThumbs`). The key is built from what the thumbnails depend on
    // — NOT from `thumbEditedUrl` — so storing an edited thumbnail does not trigger another round.
    const thumbSourcesRef = useRef(new Map()); // page id → { url, data }: the decoded plain thumbnail
    const thumbMadeRef = useRef(new Map()); // page id → signature its current thumbEditedUrl was made from
    const thumbKey = useMemo(
        () => (adjustThumbs ? pages.map((p) => `${p.id}|${p.thumbUrl ? thumbSignature(p) : ''}`).join('\n') : ''),
        [adjustThumbs, pages],
    );
    useEffect(() => {
        if (!adjustThumbs) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            const live = new Set(pagesRef.current.map((p) => p.id));
            [...thumbSourcesRef.current.keys()].forEach((id) => { if (!live.has(id)) thumbSourcesRef.current.delete(id); });
            [...thumbMadeRef.current.keys()].forEach((id) => { if (!live.has(id)) thumbMadeRef.current.delete(id); });

            for (const page of pagesRef.current) {
                if (cancelled) return;
                if (!page.thumbUrl) continue;
                const signature = thumbSignature(page);
                if (thumbMadeRef.current.get(page.id) === signature) continue;
                let edited = null;
                try {
                    if (!isUntouched(page)) {
                        let source = thumbSourcesRef.current.get(page.id);
                        if (!source || source.url !== page.thumbUrl) {
                            source = { url: page.thumbUrl, data: await decodeThumb(page.thumbUrl) };
                            thumbSourcesRef.current.set(page.id, source);
                        }
                        if (cancelled) return;
                        edited = imageDataToDataUrl(
                            processDocument(source.data, { preset: page.preset, adjustments: page.adjustments }),
                            'image/jpeg',
                            0.7,
                        );
                    }
                } catch {
                    edited = null; // the plain thumbnail simply stays
                }
                thumbMadeRef.current.set(page.id, signature);
                patchPage(page.id, { thumbEditedUrl: edited });
                await new Promise((resolve) => setTimeout(resolve, 0)); // let the browser paint between pages
            }
        }, 120);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [thumbKey, adjustThumbs, patchPage]);

    // `tag` (optional) is merged into every page this import adds — e.g. { booth: { id, isPrivate } } for a file that came out of the
    // Photo Booth, so the tool can tell later which pages belong to which customer file. → the ids of the pages added
    const addFiles = useCallback(async (incoming, tag) => {
        const room = MAX_PAGES - pagesRef.current.length;
        if (room <= 0) { onError?.('tooMany'); return []; }

        setImporting(true);
        try {
            const { pages: fresh, errors } = await pagesFromFiles(incoming, { room });
            if (errors.length) onError?.(errors[0].reason);
            if (!fresh.length) return [];
            const added = tag ? fresh.map((page) => ({ ...page, ...tag })) : fresh;

            setPages((prev) => [...prev, ...added]);
            // First import selects its first page; a later one leaves the user
            // where they were, so adding a page mid-edit does not throw away the
            // page they were tuning.
            if (!selectedRef.current) setSelectedId(added[0].id);
            return added.map((page) => page.id);
        } finally {
            setImporting(false);
        }
    }, [onError]);

    const removePage = useCallback((id) => {
        dropEntry(id);
        setPages((prev) => {
            const next = prev.filter((p) => p.id !== id);
            if (selectedRef.current === id) {
                const gone = prev.findIndex((p) => p.id === id);
                const fallback = next[Math.min(gone, next.length - 1)] || null;
                setSelectedId(fallback?.id ?? null);
                if (!fallback) {
                    setPreviewResult(null);
                    setSourceSize(null);
                    setBaseUrl(null);
                }
            }
            return next;
        });
    }, [dropEntry]);

    const reorder = useCallback((fromId, toId) => {
        setPages((prev) => {
            const from = prev.findIndex((p) => p.id === fromId);
            const to = prev.findIndex((p) => p.id === toId);
            if (from < 0 || to < 0 || from === to) return prev;
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    }, []);

    const clear = useCallback(() => {
        [...cacheRef.current.keys()].forEach(dropEntry);
        setPages([]);
        setSelectedId(null);
        setPreviewResult(null);
        setSourceSize(null);
        setBaseUrl(null);
        setCompare(false);
    }, [dropEntry]);

    const setPreset = useCallback((preset) => {
        if (!selectedRef.current) return;
        setCompare(false);
        patchPage(selectedRef.current, { preset });
    }, [patchPage]);

    const setAdjustment = useCallback((key, value) => {
        const id = selectedRef.current;
        if (!id) return;
        setPages((prev) => prev.map((p) => (
            p.id === id ? { ...p, adjustments: { ...p.adjustments, [key]: value } } : p
        )));
    }, []);

    const resetAdjustments = useCallback(() => {
        if (!selectedRef.current) return;
        patchPage(selectedRef.current, { adjustments: { ...DEFAULT_ADJUSTMENTS } });
    }, [patchPage]);

    /**
     * Push the selected page's cleanup onto every other page.
     *
     * Only the CLEANUP travels — preset and sliders. Rotation and the crop quad
     * are about one photograph's framing, and copying them onto the rest would
     * mangle pages that were shot straight.
     */
    const applyToAll = useCallback(() => {
        const lead = pagesRef.current.find((p) => p.id === selectedRef.current);
        if (!lead) return;
        setPages((prev) => prev.map((p) => (
            p.id === lead.id
                ? p
                : { ...p, preset: lead.preset, adjustments: { ...lead.adjustments } }
        )));
    }, []);

    const rotate = useCallback(() => {
        const page = pagesRef.current.find((p) => p.id === selectedRef.current);
        if (!page || busy) return;

        const entry = cacheRef.current.get(page.id);
        const original = entry?.original;
        const swapped = page.rotation % 2 === 1;
        const baseW = original ? (swapped ? original.height : original.width) : 0;
        const baseH = original ? (swapped ? original.width : original.height) : 0;
        // The quad lives in base coordinates, so it turns with the page or it
        // would point at the wrong corner.
        const quad = page.quad && original ? rotateQuad(page.quad, 1, baseW, baseH) : page.quad;
        const next = { ...page, rotation: (page.rotation + 1) % 4, quad };

        patchPage(page.id, { rotation: next.rotation, quad: next.quad });
        void refreshSelected(next);
    }, [busy, patchPage, refreshSelected]);

    const applyCrop = useCallback((quad) => {
        const page = pagesRef.current.find((p) => p.id === selectedRef.current);
        if (!page) return;
        const next = { ...page, quad };
        patchPage(page.id, { quad });
        void (async () => {
            await nextFrame();
            await refreshSelected(next);
        })();
    }, [patchPage, refreshSelected]);

    /**
     * One page at full resolution, cleaned. Used by download, print and every
     * batch export, so what leaves the tool is what the preview showed.
     */
    const renderPageFull = useCallback(async (pageId) => {
        const page = pagesRef.current.find((p) => p.id === pageId);
        if (!page) return null;
        const entry = cacheRef.current.get(pageId) || await buildEntry(page);
        await nextFrame();
        const capped = downscaleImageData(entry.source, EXPORT_MAX_DIM);
        return processDocument(capped, { preset: page.preset, adjustments: page.adjustments });
    }, [buildEntry]);

    const previewSource = cacheRef.current.get(selectedId)?.previewSource ?? null;

    return {
        pages,
        selected,
        selectedId,
        select: setSelectedId,
        addFiles,
        removePage,
        reorder,
        clear,
        previewResult,
        previewSource,
        baseUrl,
        sourceSize,
        busy,
        setBusy,
        importing,
        compare,
        setCompare,
        setPreset,
        setAdjustment,
        resetAdjustments,
        applyToAll,
        rotate,
        applyCrop,
        renderPageFull,
    };
}
